'use client';

import React, { useState, useEffect, useRef } from 'react';
import Image from 'next/image';

type GirlPose = 'A1' | 'A2' | 'A3' | 'A4';
type BoyPose = 'B1' | 'B2' | 'B3' | 'B4';
type ActionPhase = 'idle' | 'punching' | 'kicking';

const GIRL_IMAGES: Record<GirlPose, string> = {
  A1: '/assets/images/A1-1780221488172.png',
  A2: '/assets/images/A2-1780221488171.png',
  A3: '/assets/images/A3-1780221488159.png',
  A4: '/assets/images/A4-1780221488008.png'
};

const BOY_IMAGES: Record<BoyPose, string> = {
  B1: '/assets/images/B1-1780221746476.png',
  B2: '/assets/images/B2-1780221746461.png',
  B3: '/assets/images/B3-1780221746469.png',
  B4: '/assets/images/B4-1780221746350.png'
};

const BOY_DIALOGUES = [
'Will never drink and drive 🙏',
'I am sorry tyagi 😢',
'sorry yaara 😔',
'Please yaar sorry 🙏',
'maafi tyagi 😭',
'maaf karde yaar 😭'];


function getBoyPose(hits: number): BoyPose {
  if (hits >= 5) return 'B4';
  if (hits >= 3) return 'B3';
  if (hits >= 1) return 'B2';
  return 'B1';
}

export default function GameArenaContent() {
  const [girlPos, setGirlPos] = useState(12);
  const [girlPose, setGirlPose] = useState<GirlPose>('A1');
  const [boyPose, setBoyPose] = useState<BoyPose>('B1');
  const [hits, setHits] = useState(0);
  const [actionPhase, setActionPhase] = useState<ActionPhase>('idle');
  const [boyMessage, setBoyMessage] = useState(BOY_DIALOGUES[0]);
  const [showActions, setShowActions] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [shakeEffect, setShakeEffect] = useState(false);

  const actionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hitsRef = useRef(0);

  const BOY_POS = 72;
  const NEAR_THRESHOLD = 30;
  const GROUND_BOTTOM = -10;

  useEffect(() => {
    hitsRef.current = hits;
  }, [hits]);

  useEffect(() => {
    const distance = BOY_POS - girlPos;
    if (!gameOver && distance <= NEAR_THRESHOLD && distance > 0) {
      setShowActions(true);
    } else {
      setShowActions(false);
    }
  }, [girlPos, gameOver]);

  function moveLeft() {
    if (gameOver) return;
    setGirlPos((p) => Math.max(0, p - 10));
  }

  function moveRight() {
    if (gameOver) return;
    const distance = BOY_POS - girlPos;
    if (distance <= 15) return;
    setGirlPos((p) => Math.min(BOY_POS - 15, p + 10));
  }

  function performAction(type: 'punch' | 'kick') {
    if (actionPhase !== 'idle' || gameOver || !showActions) return;

    const newHits = hitsRef.current + 1;
    const pose: GirlPose = type === 'punch' ? 'A2' : 'A3';
    const newBoyPose = getBoyPose(newHits);

    setActionPhase(type === 'punch' ? 'punching' : 'kicking');
    setGirlPose(pose);
    setBoyPose(newBoyPose);
    setHits(newHits);
    hitsRef.current = newHits;

    setShakeEffect(true);
    setTimeout(() => setShakeEffect(false), 400);

    // Update dialogue: index = newHits (capped at last dialogue)
    const dialogueIndex = Math.min(newHits, BOY_DIALOGUES.length - 1);
    setBoyMessage(BOY_DIALOGUES[dialogueIndex]);

    if (actionTimerRef.current) clearTimeout(actionTimerRef.current);
    actionTimerRef.current = setTimeout(() => {
      if (newHits >= 5) {
        setGirlPose('A4');
        setGirlPos((p) => Math.max(0, p - 12));
        setGameOver(true);
        setShowActions(false);
        setActionPhase('idle');
      } else {
        setGirlPose('A1');
        setActionPhase('idle');
      }
    }, 600);
  }

  function resetGame() {
    setGirlPos(12);
    setGirlPose('A1');
    setBoyPose('B1');
    setHits(0);
    hitsRef.current = 0;
    setActionPhase('idle');
    setBoyMessage(BOY_DIALOGUES[0]);
    setShowActions(false);
    setGameOver(false);
    setShakeEffect(false);
  }

  const actionDisabled = !showActions || actionPhase !== 'idle' || gameOver;

  return (
    <div
      className="min-h-screen flex flex-col items-center select-none overflow-hidden relative"
      style={{
        backgroundImage: 'url("/assets/images/20c1f8f4-f7ac-4d5c-985d-52ada13c5b00-1780225842060-1780227037437.jpg")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        justifyContent: 'center',
        paddingTop: '50px'
      }}>
      
      {/* Dark overlay for contrast */}
      <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.18)', zIndex: 0 }} />

      {/* Game content */}
      <div className="relative z-10 w-full flex flex-col items-center gap-3">

        {/* Play Again button — floats above arena, does not affect layout */}
        <div style={{ height: '52px', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', width: '100%' }}>
          {gameOver &&
            <div className="text-center px-4" style={{ marginBottom: '-8px' }}>
              <button
                onClick={resetGame}
                className="px-8 py-2.5 bg-green-500 hover:bg-green-400 active:scale-95 text-white font-bold rounded-xl shadow-lg transition-all duration-150 uppercase tracking-wider border-2 border-green-300">
                🔄 Play Again
              </button>
            </div>
          }
        </div>

        {/* Arena: characters on ground */}
        <div className="w-full relative" style={{ height: '260px' }}>

          {/* Girl Character */}
          <div
            className="absolute transition-[left] duration-300 ease-out"
            style={{
              left: `${girlPos}%`,
              bottom: `${GROUND_BOTTOM}px`,
              transform: 'translateX(-50%)',
              zIndex: 5
            }}>
            
            <Image
              src={GIRL_IMAGES[girlPose]}
              alt="Girl character"
              width={72}
              height={120}
              style={{ display: 'block', objectFit: 'contain' }}
              className="drop-shadow-md"
              priority />
            
          </div>

          {/* Boy Character */}
          <div
            className={`absolute transition-[left] duration-300 ${shakeEffect ? 'translate-x-1' : ''}`}
            style={{
              left: `${BOY_POS}%`,
              bottom: `${GROUND_BOTTOM}px`,
              transform: 'translateX(-50%)',
              zIndex: 5,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}>
            
            {/* Speech Bubble */}
            <div
              style={{
                marginBottom: '6px',
                minWidth: '150px',
                maxWidth: '190px',
                pointerEvents: 'none'
              }}>
              
              <div
                className="px-3 py-2 rounded-2xl text-sm font-bold text-center leading-snug shadow-lg"
                style={{
                  background: 'white',
                  border: '2.5px solid #2ecc71',
                  color: '#1a1a1a',
                  borderRadius: '16px'
                }}>
                
                {boyMessage}
              </div>
              <div style={{ position: 'relative', height: '12px', display: 'flex', justifyContent: 'center' }}>
                <div
                  style={{
                    width: 0,
                    height: 0,
                    borderLeft: '9px solid transparent',
                    borderRight: '9px solid transparent',
                    borderTop: '12px solid #2ecc71'
                  }} />
                
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    width: 0,
                    height: 0,
                    borderLeft: '7px solid transparent',
                    borderRight: '7px solid transparent',
                    borderTop: '10px solid white'
                  }} />
                
              </div>
            </div>

            <Image
              src={BOY_IMAGES[boyPose]}
              alt="Boy character"
              width={72}
              height={120}
              style={{ display: 'block', objectFit: 'contain' }}
              className="drop-shadow-md"
              priority />
            
          </div>
        </div>

        {/* Controls */}
        <div className="w-full flex flex-col items-center pb-2 pt-1 gap-3">
          {/* Left / Right row */}
          <div className="flex gap-4">
            <button
              onClick={moveLeft}
              disabled={gameOver}
              className="flex items-center gap-1.5 px-7 py-3 rounded-xl font-bold text-base text-white shadow-md active:scale-95 transition-all duration-150"
              style={{
                background: gameOver ? '#93b4d4' : '#3b82f6',
                minWidth: '110px',
                justifyContent: 'center',
                opacity: gameOver ? 0.6 : 1,
                cursor: gameOver ? 'not-allowed' : 'pointer'
              }}
              aria-label="Move left">
              
              <span style={{ fontSize: '18px' }}>◀</span> Left
            </button>
            <button
              onClick={moveRight}
              disabled={gameOver}
              className="flex items-center gap-1.5 px-7 py-3 rounded-xl font-bold text-base text-white shadow-md active:scale-95 transition-all duration-150"
              style={{
                background: gameOver ? '#93b4d4' : '#3b82f6',
                minWidth: '110px',
                justifyContent: 'center',
                opacity: gameOver ? 0.6 : 1,
                cursor: gameOver ? 'not-allowed' : 'pointer'
              }}
              aria-label="Move right">
              
              Right <span style={{ fontSize: '18px' }}>▶</span>
            </button>
          </div>

          {/* Punch / Kick row */}
          <div className="flex gap-4">
            <button
              onClick={() => performAction('punch')}
              disabled={actionDisabled}
              className="flex flex-col items-center justify-center px-7 py-3 rounded-xl font-bold text-base shadow-md active:scale-95 transition-all duration-150"
              style={{
                minWidth: '110px',
                background: actionDisabled ? '#b5a07a' : '#c9a96e',
                color: actionDisabled ? '#e8d5b0' : 'white',
                cursor: actionDisabled ? 'not-allowed' : 'pointer',
                opacity: actionDisabled ? 0.7 : 1
              }}
              aria-label="Punch">
              
              <span style={{ fontSize: '20px' }}>👊</span>
              <span className="tracking-widest text-sm mt-0.5">PUNCH</span>
            </button>
            <button
              onClick={() => performAction('kick')}
              disabled={actionDisabled}
              className="flex flex-col items-center justify-center px-7 py-3 rounded-xl font-bold text-base shadow-md active:scale-95 transition-all duration-150"
              style={{
                minWidth: '110px',
                background: actionDisabled ? '#7ab8d4' : '#5aabcf',
                color: actionDisabled ? '#c0dff0' : 'white',
                cursor: actionDisabled ? 'not-allowed' : 'pointer',
                opacity: actionDisabled ? 0.7 : 1
              }}
              aria-label="Kick">
              
              <span style={{ fontSize: '20px' }}>🦵</span>
              <span className="tracking-widest text-sm mt-0.5">KICK</span>
            </button>
          </div>
        </div>
      </div>
    </div>);

}

const MoveType: React.FC = () => {
  React.useEffect(() => {
    // eslint-disable-next-line no-console
    console.warn('Placeholder: MoveType is not implemented yet.');
  }, []);
  return (
    <div>
      {/* MoveType placeholder */}
    </div>);

};

export { MoveType };